/**
 * Input Filter Utility
 * Provides character filtering for form inputs based on schema configuration
 *
 * Usage:
 * 1. Add to schema: "ui": { "inputFilter": "numeric", "maxLength": 12 }
 * 2. Import in component: import { applyInputFilter } from '@utils/input-filter';
 * 3. Use in template: (input)="applyInputFilter($event, control.ui.inputFilter, control.ui.maxLength, control.qname, filingForm)"
 */

import { FormGroup } from '@angular/forms';

import { TRIM_LEADING_ZERO_QNAMES } from './schema-normalizers.util';

/**
 * Strip every leading zero from a value.
 *
 * Used by the BRN field (`ssmt-dei:NewCompanyRegistrationNumber`), which is
 * capped at 12 characters: a leading zero would consume a slot and push a real
 * digit past the cap, silently corrupting the registration number that feeds
 * the XBRL entity identifier and the download filename.
 *
 * An all-zeroes value collapses to empty — a BRN of "0" is meaningless, and the
 * required-validator surfaces the empty field to the user.
 *
 * Idempotent: the keystroke seam and the state seam both apply it to a typed
 * value, so `f(f(x)) === f(x)` is a load-bearing property, not a nicety.
 */
export function trimLeadingZeros(value: string): string {
  return value.replace(/^0+/, '');
}

/**
 * Re-apply the schema-declared leading-zero normalisers to a live form group,
 * writing the trimmed value back into the control.
 *
 * ## Why this exists — the form/state divergence bug
 *
 * `FilingFormStateService.applyValueNormalizersToState` normalises the BRN on
 * every state write. But the form control it came from was never updated, so
 * the two drifted apart:
 *
 * - form holds `030188812341` (12 digits) → `form.valid` is **true**, Submit enabled
 * - state holds `30188812341`  (11 digits) → what actually gets submitted
 *
 * The sheet's `isValid` flag and the Submit gate are both computed from the
 * *form*, so a value the trim had already invalidated sailed through. The
 * `"Please enter 12 digit new company registration number."` error only
 * appeared on the *next* visit, once the form was patched back from state —
 * which read as "an error that wasn't there before I re-submitted".
 *
 * Calling this at the start of the debounced persist step keeps form and state
 * in agreement, so validation runs against the value that will actually be
 * submitted and the Submit button closes at edit time instead of a visit later.
 *
 * Uses `emitEvent: false` so writing back does not re-trigger the very
 * `valueChanges` pipeline that called it. Validators still re-run (Angular
 * suppresses the *event*, not the recomputation), and the view is still
 * updated, so the user sees the corrected value and its error immediately.
 *
 * @returns `true` if any control was rewritten — useful for logging/tests.
 */
export function applyTrimNormalizersToForm(form: FormGroup): boolean {
  let changed = false;

  TRIM_LEADING_ZERO_QNAMES.forEach((qname) => {
    const control = form.get(qname);
    if (!control) return;

    const value = control.value;
    if (typeof value !== 'string') return;

    const trimmed = trimLeadingZeros(value);
    if (trimmed === value) return;

    control.setValue(trimmed, { emitEvent: false });
    changed = true;
  });

  return changed;
}

/**
 * Strip every character the filter type disallows. Character-wise and
 * order-preserving, which is what lets {@link projectCaret} run it over a
 * prefix of the value and get a comparable result.
 */
function filterByType(value: string, filterType: string): string {
  switch (filterType) {
    case 'numeric':
      // Only allow digits 0-9
      // Handles pasted text like "123123123-.434534" by removing all non-digits
      // Result: "123123123434534", then truncated to maxLength (e.g., 12 digits)
      return value.replace(/[^0-9]/g, '');
    case 'alpha':
      // Only allow letters A-Z, a-z
      return value.replace(/[^A-Za-z]/g, '');
    case 'alphanumeric':
      // Only allow letters and digits
      return value.replace(/[^A-Za-z0-9]/g, '');
    case 'alphanumeric-space':
      // Only allow letters, digits, and spaces
      return value.replace(/[^A-Za-z0-9 ]/g, '');
    case 'alphanumeric-dash':
      // Only allow letters, digits, hyphens, and underscores
      return value.replace(/[^A-Za-z0-9\-_]/g, '');
    default:
      // Unknown filter type - no filtering
      console.warn(`Unknown input filter type: ${filterType}`);
      return value;
  }
}

/**
 * The full keystroke pipeline: filter → trim leading zeroes → cap at maxLength.
 *
 * Trimming happens BEFORE the cap. Order matters: capping '00202001012345' to
 * 12 first would yield '002020010123', and trimming that leaves '2020010123' —
 * two real digits silently destroyed.
 */
function normalizeValue(
  value: string,
  filterType: string,
  maxLength: number | undefined,
  trimLeadingZerosFlag?: boolean
): string {
  let next = filterByType(value, filterType);
  if (trimLeadingZerosFlag) next = trimLeadingZeros(next);
  if (maxLength && next.length > maxLength) next = next.substring(0, maxLength);
  return next;
}

/**
 * Where the caret belongs after the value was rewritten.
 *
 * ## Why the naive answer is wrong
 *
 * Assigning `input.value` resets the caret to the end of the field. On a field
 * that only ever *appends* that is invisible, but on the BRN field the user
 * types a `0` at position 0 of an existing number — the trim deletes exactly
 * that keystroke, and the caret is thrown to the end of a 12-digit value. The
 * next digit typed then lands at the wrong end of the number.
 *
 * ## How this computes it
 *
 * The pipeline is character-wise and order-preserving, so the text that ends up
 * *before* the caret is exactly the pipeline applied to the text that was
 * before the caret beforehand. Run the same {@link normalizeValue} over the
 * pre-caret prefix and its length IS the new caret offset — no diffing, no
 * counting of removed characters, and it stays correct for the filter and the
 * maxLength cap as well as the trim.
 *
 * Worked example — "202001012345" with a `0` typed at the front:
 *   raw = "0202001012345", caret = 1  → prefix "0" → normalised ""  → caret 0
 * The caret stays put, so a second keystroke lands where the user is looking.
 */
function projectCaret(
  rawValue: string,
  caret: number,
  filterType: string,
  maxLength: number | undefined,
  trimLeadingZerosFlag?: boolean
): number {
  return normalizeValue(rawValue.substring(0, caret), filterType, maxLength, trimLeadingZerosFlag).length;
}

/**
 * Move the caret without disturbing anything else.
 *
 * `setSelectionRange` throws a DOMException on input types that do not support
 * a text selection (`number`, `email`, …). None of the filtered fields use
 * those types today, but a schema change could introduce one, and a thrown
 * error inside an `(input)` handler would break the keystroke entirely — so the
 * failure is swallowed and the caret is left wherever the browser put it.
 */
function setCaret(input: HTMLInputElement, position: number): void {
  try {
    input.setSelectionRange(position, position);
  } catch {
    /* input type does not support selection — leave the caret alone */
  }
}

/**
 * Apply input filter based on schema configuration
 * Supports: numeric, alpha, alphanumeric, alphanumeric-space
 *
 * @param event - Input event from the text field
 * @param filterType - Type of filter to apply ('numeric' | 'alpha' | 'alphanumeric' | 'alphanumeric-space')
 * @param maxLength - Maximum length of input (optional)
 * @param qname - Form control name (qname from schema)
 * @param formGroup - The FormGroup containing the control
 */
export function applyInputFilter(
  event: Event,
  filterType: string | undefined,
  maxLength: number | undefined,
  qname: string,
  formGroup: FormGroup,
  trimLeadingZerosFlag?: boolean
): void {
  if (!filterType) return; // No filter specified

  const input = event.target as HTMLInputElement;
  const rawValue = input.value;
  // Read the caret BEFORE the value is rewritten — assigning `input.value`
  // collapses the selection to the end of the field. `null` on input types that
  // expose no selection, in which case we simply do not reposition.
  const rawCaret = input.selectionStart;

  const value = normalizeValue(rawValue, filterType, maxLength, trimLeadingZerosFlag);

  // Update the form control value
  const control = formGroup.get(qname);
  if (control) {
    control.setValue(value, { emitEvent: false });
    // Write the input element directly: Angular suppressed the valueChanges
    // event above, so the DOM would otherwise keep the unfiltered text.
    if (input.value !== value) {
      input.value = value;
    }
    if (rawCaret !== null) {
      setCaret(input, projectCaret(rawValue, rawCaret, filterType, maxLength, trimLeadingZerosFlag));
    }
  }
}

/**
 * Handle paste event with input filtering
 * Intercepts pasted text, applies filter, and updates the input
 * Example: Pasting "123123123-.434534" with numeric filter and maxLength=12
 * Result: "123123123434" (removes '-' and '.', keeps only digits, truncates to 12)
 *
 * @param event - Clipboard paste event
 * @param filterType - Type of filter to apply
 * @param maxLength - Maximum length of input (optional)
 * @param qname - Form control name (qname from schema)
 * @param formGroup - The FormGroup containing the control
 */
export function onPasteFilter(
  event: ClipboardEvent,
  filterType: string | undefined,
  maxLength: number | undefined,
  qname: string,
  formGroup: FormGroup,
  trimLeadingZerosFlag?: boolean
): void {
  if (!filterType) return;

  event.preventDefault();

  const pastedText = event.clipboardData?.getData('text') || '';

  // Same pipeline as the keystroke seam — filter, trim, then cap.
  // A paste REPLACES the whole field rather than inserting at the caret, so
  // there is no caret to preserve here: the browser leaves it at the end of the
  // new value, which is where the user expects it.
  const filteredValue = normalizeValue(pastedText, filterType, maxLength, trimLeadingZerosFlag);

  // Update the form control value
  const control = formGroup.get(qname);
  if (control) {
    control.setValue(filteredValue);
    control.markAsTouched();
  }
}

/**
 * Input filter types supported by the utility
 */
export type InputFilterType = 'numeric' | 'alpha' | 'alphanumeric' | 'alphanumeric-space' | 'alphanumeric-dash';
